import { useEffect, useMemo, useState } from 'react';
import CustomerDetailsModal from '../../pages/customers/CustomerDetailsModal';
import MemberCustomerPanel from '../MemberCustomerPanel/MemberCustomerPanel';
import { isLeadOnlyMember } from '../../utils/marketingApi';
import { memberCustomerId, resolveCustomerIdByPhone } from '../../utils/resolveCustomerIdByPhone';

/**
 * Call Manager row panel:
 * - Customers → same CustomerDetailsModal as Customers / Leads (shared details API + activity log)
 * - Leads (no customer) → legacy MemberCustomerPanel
 */
export default function CallManagerMemberPanel({
  isOpen,
  onClose,
  member,
  batchId,
  customerApiBase,
  authToken,
  onViewAllCalls,
  activityRefreshKey = 0,
  showToast,
  onActionRecorded,
  onConverted,
  onRemarksSaved,
  stackAboveOverlay = false,
}) {
  const [customerId, setCustomerId] = useState(null);
  const [resolving, setResolving] = useState(false);

  const authHeaders = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h.Authorization = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  const memberPhone = (member?.phone_e164 || member?.phone || '').trim();
  const effectiveBatchId = batchId || member?.batch_id;

  useEffect(() => {
    if (!isOpen || !member) {
      setCustomerId(null);
      setResolving(false);
      return undefined;
    }

    let cancelled = false;
    const direct = memberCustomerId(member);
    if (direct) {
      setCustomerId(direct);
      setResolving(false);
      return undefined;
    }

    setResolving(true);
    setCustomerId(null);
    resolveCustomerIdByPhone(memberPhone, authHeaders).then((id) => {
      if (cancelled) return;
      setCustomerId(id);
      setResolving(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, member, member?.customer_id, member?.is_lead_only, member?.outcome, memberPhone, authHeaders]);

  const leadPanelMember = useMemo(
    () => (member ? {
      ...member,
      batch_voice_target: member.batch_voice_target || '',
      batch_completion_source: member.batch_completion_source || '',
    } : null),
    [
      member?.id,
      member?.phone_e164,
      member?.phone,
      member?.customer_id,
      member?.batch_voice_target,
      member?.batch_completion_source,
      member?.captured_whatsapp_number,
      member?.name,
      member?.business_name,
      member?.voice_contact_name,
      member?.voice_business_label,
    ],
  );

  if (!isOpen || !member) return null;

  const showCustomerModal = !!customerId;
  // Legacy panel only when no CRM customer exists yet (leads / not-yet-converted members).
  const showLeadPanel = !showCustomerModal && !resolving && isLeadOnlyMember(member);

  if (showLeadPanel) {
    return (
      <MemberCustomerPanel
        isOpen
        onClose={onClose}
        member={leadPanelMember}
        customerApiBase={customerApiBase}
        authToken={authToken}
        batchId={effectiveBatchId}
        marketingInitiated={false}
        showCall
        showToast={showToast}
        onActionRecorded={onActionRecorded}
        onConverted={onConverted}
        onRemarksSaved={onRemarksSaved}
      />
    );
  }

  if (showCustomerModal) {
    return (
      <CustomerDetailsModal
        isOpen
        toggle={onClose}
        customerId={customerId}
        onViewAllCalls={onViewAllCalls}
        activityRefreshKey={activityRefreshKey}
        stackAboveOverlay={stackAboveOverlay}
      />
    );
  }

  return null;
}
